import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FadeUp } from '../components/Motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { resolveImg } from '../lib/img';

const FILTERS = ['all', 'farm', 'products', 'events'];

export default function Gallery() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get('/gallery').then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (filter === 'all' ? photos : photos.filter((p) => p.category === filter)),
    [photos, filter]
  );

  const openIdx = active != null ? filtered.findIndex((p) => p.id === active) : -1;
  const close = () => setActive(null);
  const prev = () => setActive(filtered[(openIdx - 1 + filtered.length) % filtered.length]?.id);
  const next = () => setActive(filtered[(openIdx + 1) % filtered.length]?.id);

  useEffect(() => {
    const onKey = (e) => {
      if (active == null) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <main data-testid="gallery-page" className="pt-32">
      <section className="container-x max-w-7xl mx-auto pb-12">
        <FadeUp>
          <p className="overline">{t('gallery.overline')}</p>
          <h1 className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl text-ink tracking-tight max-w-4xl">
            {t('gallery.title')}
          </h1>
          <p className="mt-6 text-lg text-ink-soft max-w-2xl">{t('gallery.intro')}</p>
        </FadeUp>
      </section>

      <section className="container-x max-w-7xl mx-auto">
        <div className="flex flex-wrap gap-2 mb-10">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`gallery-filter-${f}`}
              className={`text-sm px-4 py-2 rounded-full border transition-all ${
                filter === f
                  ? 'bg-forest text-bone border-forest'
                  : 'bg-white text-ink-soft border-border-soft hover:border-forest hover:text-forest'
              }`}
            >
              {t(`gallery.${f}`)}
            </button>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pb-24">
          {filtered.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              data-testid={`gallery-item-${p.id}`}
              className={`group block overflow-hidden rounded-3xl bg-sand ${i % 5 === 0 ? 'lg:row-span-2 aspect-[3/4]' : 'aspect-[4/3]'}`}
            >
              <img
                src={resolveImg(p.image)}
                alt={p[`caption_${lng}`] || ''}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </button>
          ))}
        </div>
      </section>

      {active != null && filtered[openIdx] && (
        <div
          className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md grid place-items-center p-4"
          onClick={close}
          data-testid="lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-6 right-6 text-bone/80 hover:text-bone"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-bone/80 hover:text-bone"
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft size={36} />
          </button>
          <div className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img src={resolveImg(filtered[openIdx].image)} alt="" className="max-h-[80vh] w-auto rounded-2xl" />
            {filtered[openIdx][`caption_${lng}`] && (
              <p className="mt-4 text-bone/80 text-center text-sm">{filtered[openIdx][`caption_${lng}`]}</p>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-bone/80 hover:text-bone"
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}
    </main>
  );
}
